import { getErrorMessage } from '../../utils/errorMessage';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, Check, CircleAlert, FileInput, Film, Grid2X2, Loader2, Plus, Sparkles, X } from 'lucide-react';
import api from '../../utils/api';
import { getPublishedVideoCases } from '../learn/videoCaseApi';
import { checkUploadFile, uploadFileToNode, UPLOAD_LIMITS } from './canvasUpload';
import CanvasSourcePicker from './CanvasSourcePicker';
import ExternalScriptImport from './ExternalScriptImport.jsx';
import { externalScriptSelection, MAX_EXTERNAL_SCRIPT_CHARS } from './externalScriptText';
import CanvasScriptPrecheck from './CanvasScriptPrecheck';
import useCanvasScriptPrecheck from './useCanvasScriptPrecheck';
import { clearScriptBreakdown, queueScriptBreakdown } from './canvasScriptBreakdownReceipt';
import './NewCanvasDialog.css';

const CHOICES = [
  ['blank', '空白画布', '自由添加节点，开始新的创作。', Grid2X2],
  ['script', '从剧本创建', '选择我的剧本，或导入外部正文。', Film],
  ['case', '从案例创建', '带入已发布案例的素材与创作说明。', Sparkles],
  ['import', '导入媒体', '图片、视频和音频直接成为素材节点。', FileInput],
];
const requestKey = () => window.crypto?.randomUUID?.() || `canvas-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export const canProduceEpisode = episode => Boolean(episode?.has_content && ['final', 'refined'].includes(episode.status));

export default function NewCanvasDialog({ preview = false, initialName = '', initialProjectId = '', initialEpisode = '', initialCaseId = '', onClose, onCreated }) {
  const [mode, setMode] = useState(initialProjectId ? 'script' : initialCaseId ? 'case' : 'blank');
  const [scriptSource, setScriptSource] = useState('library');
  const [externalDraft, setExternalDraft] = useState({ text: '', fileName: '', selectedId: '' });
  const [externalReading, setExternalReading] = useState(false);
  const externalSelection = useMemo(() => externalScriptSelection(externalDraft), [externalDraft]);
  const libraryScript = mode === 'script' && scriptSource === 'library';
  const externalScript = mode === 'script' && scriptSource === 'external';
  const [name, setName] = useState(initialName);
  const [nameValidationRequested, setNameValidationRequested] = useState(false);
  const nameInvalid = nameValidationRequested && !name.trim();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [episodes, setEpisodes] = useState([]);
  const [episodeNumber, setEpisodeNumber] = useState(String(initialEpisode || ''));
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState(String(initialCaseId || ''));
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [episodeLoading, setEpisodeLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [episodeError, setEpisodeError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [retry, setRetry] = useState(0);
  const [created, setCreated] = useState(null);
  const dialogRef = useRef(null);
  const nameRef = useRef(null);
  const inFlight = useRef(false);
  const submission = useRef(null);
  const nameEdited = useRef(false);

  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.querySelector('input')?.focus();
    return () => previous?.focus?.();
  }, []);

  useEffect(() => {
    let active = true;
    setLoadError('');
    if ((!libraryScript && mode !== 'case') || preview) { setLoading(false); return undefined; }
    setLoading(true);
    const request = mode === 'script'
      ? api.get('/agent/projects').then(response => (Array.isArray(response.data) ? response.data : []).filter(project => !project.writing_form || project.writing_form === 'short_drama'))
      : getPublishedVideoCases();
    request.then(rows => { if (active) (mode === 'script' ? setProjects : setCases)(rows); })
      .catch(() => { if (active) setLoadError(getErrorMessage(mode === 'script' ? '剧本列表加载失败，请重试。' : '案例列表加载失败，请重试。', '案例列表加载失败，请重试。')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [mode, libraryScript, preview, retry]);

  useEffect(() => {
    let active = true;
    setEpisodes([]);
    setEpisodeError('');
    if (!projectId || preview || !libraryScript) { setEpisodeLoading(false); return undefined; }
    setEpisodeLoading(true);
    api.get(`/agent/projects/${projectId}`).then(({ data }) => {
      if (active) setEpisodes(Array.isArray(data.episodes) ? data.episodes : []);
    }).catch(() => { if (active) setEpisodeError('集数加载失败，请重试。'); })
      .finally(() => { if (active) setEpisodeLoading(false); });
    return () => { active = false; };
  }, [projectId, libraryScript, preview, retry]);

  const project = projects.find(item => String(item.id) === String(projectId));
  const episode = episodes.find(item => String(item.episode_number) === episodeNumber);
  const selectedCase = cases.find(item => String(item.id) === caseId);
  const availableEpisodes = episodes.filter(canProduceEpisode);
  const scriptInput = externalScript && !externalReading && externalSelection.content && externalDraft.text.length <= MAX_EXTERNAL_SCRIPT_CHARS
    ? { type: 'external_script', content: externalSelection.content, file_name: externalDraft.fileName, episode_label: externalSelection.episodeLabel }
    : libraryScript && !loading && !episodeLoading && !loadError && !episodeError && project && canProduceEpisode(episode)
      ? { type: 'script', project_id: projectId, episode_number: Number(episodeNumber), revision: Number(episode.revision || 0) } : null;
  const precheck = useCanvasScriptPrecheck(scriptInput, preview);
  useEffect(() => {
    if (nameEdited.current) return;
    if (externalScript) setName(`${externalDraft.fileName.replace(/\.[^.]+$/, '') || '外部剧本'} · ${externalSelection.episodeLabel || '我的创作'}`.slice(0, 200));
    else if (libraryScript && project && episode) setName(`${project.title} · 第 ${episode.episode_number} 集`.slice(0, 200));
    else if (mode === 'case' && selectedCase) setName(`${selectedCase.title} · 我的创作`.slice(0, 200));
  }, [mode, libraryScript, externalScript, externalDraft.fileName, externalSelection.episodeLabel, project, episode, selectedCase]);

  const chooseFiles = event => {
    const selected = Array.from(event.target.files || []);
    if (!selected.length) return;
    if (selected.length > 20) { setError('一次最多导入 20 个文件'); return; }
    const checked = selected.map(file => ({ file, ...checkUploadFile(file) }));
    const invalid = checked.find(item => !item.ok);
    if (invalid) { setError(getErrorMessage(`${invalid.file.name}：${invalid.reason}`)); return; }
    setFiles(checked.map(item => ({ file: item.file, type: item.rawType, status: 'pending' })));
    setError('');
  };

  const ready = !busy && (mode === 'blank'
    || (mode === 'script' && precheck.passed)
    || (mode === 'case' && !loading && !loadError && selectedCase) || (mode === 'import' && files.length));

  const create = async (event, startBreakdown = mode === 'script') => {
    event.preventDefault();
    if (inFlight.current || !ready) return;
    if (!name.trim()) {
      setNameValidationRequested(true);
      nameRef.current?.focus();
      return;
    }
    if (preview) { setError('当前为界面预览，请登录后创建真实画布。'); return; }
    inFlight.current = true;
    setBusy(true); setError(''); setProgress(mode === 'script' ? '正在创建画布并导入剧本…' : '正在创建画布…');
    try {
      const source = mode === 'script' ? scriptInput
        : mode === 'case' ? { type: mode, case_id: Number(caseId) }
          : mode === 'import' ? { type: mode, files: files.map(item => ({ name: item.file.name, type: item.type })) } : null;
      const payload = { name: name.trim(), ...(source ? { source } : {}) };
      const signature = JSON.stringify(payload);
      if (submission.current?.signature !== signature) submission.current = { signature, key: requestKey() };
      let canvas = created || (await api.post('/canvas', payload, { headers: { 'Idempotency-Key': submission.current.key } })).data;
      setCreated(canvas);
      if (mode === 'import') {
        let nodes = Array.isArray(canvas.initial_nodes) ? [...canvas.initial_nodes] : [];
        const findNode = (items, index) => items.find(node => node.id && node.type === files[index].type
          && node.data?.import_index === index
          && (!node.data.import_key || node.data.import_key === submission.current.key));
        if (files.some((item, index) => item.status !== 'done' && !findNode(nodes, index))) {
          setProgress('正在准备素材节点…');
          // Older create endpoints return only the canvas. Read before repairing so
          // a lost node-create response can be recovered without a duplicate node.
          const detail = (await api.get(`/canvas/${canvas.id}`)).data;
          if (!Array.isArray(detail?.nodes)) throw new Error('暂时无法读取素材节点，请重试');
          nodes = detail.nodes;
          canvas = { ...canvas, initial_nodes: nodes };
          setCreated(canvas);
        }
        const nextFiles = [...files];
        for (let index = 0; index < files.length; index += 1) {
          const item = files[index];
          let node = findNode(nodes, index);
          if (item.status === 'done' || (node?.data?.url && ['done', 'succeeded'].includes(node.data.status))) {
            nextFiles[index] = { ...item, status: 'done' }; continue;
          }
          setProgress(`正在上传 ${index + 1}/${files.length}：${item.file.name}`);
          try {
            if (!node) {
              node = (await api.post(`/canvas/${canvas.id}/nodes`, {
                type: item.type, x: 80 + (index % 4) * 360, y: 100 + Math.floor(index / 4) * 340,
                data: { label: item.file.name.slice(0, 200), source: 'upload', import_index: index,
                  import_key: submission.current.key, status: 'pending' },
              })).data;
              if (!node?.id) throw new Error('素材节点创建结果未返回，请重试');
              nodes = [...nodes, node];
              canvas = { ...canvas, initial_nodes: nodes };
              setCreated(canvas);
            }
            await uploadFileToNode(api, canvas.id, node.id, item.file, item.type);
            nextFiles[index] = { ...item, status: 'done' };
          } catch (uploadError) {
            nextFiles[index] = { ...item, status: 'failed', error: getErrorMessage(uploadError, '上传失败') };
          }
          setFiles([...nextFiles]);
        }
        if (nextFiles.some(item => item.status === 'failed')) {
          setError('画布已创建，部分媒体上传失败。重试只补传失败文件，也可以先进入画布。');
          return;
        }
      }
      if (mode === 'script') {
        if (startBreakdown) queueScriptBreakdown(canvas.id);
        else clearScriptBreakdown(canvas.id);
      }
      setProgress(startBreakdown && mode === 'script' ? '剧本已导入，正在打开画布并开始拆镜…' : '画布已创建，正在打开…');
      onCreated(canvas);
    } catch (createError) {
      if (createError.response?.data?.precheck) precheck.reject(createError.response.data.precheck);
      setError(getErrorMessage(createError, '创建暂时失败，已保留填写内容，请重试。'));
    } finally { inFlight.current = false; setBusy(false); }
  };

  const handleKeys = event => {
    if (event.key === 'Escape') { event.stopPropagation(); if (!busy) onClose(); }
    if (event.key !== 'Tab') return;
    const controls = Array.from(dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled):not([hidden]),textarea:not(:disabled),select:not(:disabled),a[href]'));
    const first = controls[0]; const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };

  return <div className="canvas-create-backdrop" onMouseDown={event => { if (!busy && event.target === event.currentTarget) onClose(); }}>
    <form className="canvas-create-dialog" role="dialog" aria-modal="true" aria-labelledby="new-canvas-title" noValidate ref={dialogRef} onSubmit={create} onKeyDown={handleKeys}>
      <header><div className="canvas-create-heading"><span className="canvas-create-emblem" aria-hidden="true"><Grid2X2 size={23} strokeWidth={1.5} /></span><div><h2 id="new-canvas-title">新建画布</h2><p>选好起点，把创意带进制作。</p></div></div><button type="button" aria-label="关闭新建画布" disabled={busy} onClick={onClose}><X size={18} /></button></header>
      <div className="canvas-create-body">
      <div className={`canvas-create-field canvas-create-name${nameInvalid ? ' is-invalid' : ''}`}>
        <label htmlFor="canvas-create-name">画布名称 <span className="canvas-create-required" aria-hidden="true">*</span></label>
        <input id="canvas-create-name" ref={nameRef} required aria-invalid={nameInvalid} aria-describedby="canvas-create-name-feedback" maxLength={200} value={name} disabled={busy || Boolean(created)} onChange={event => { nameEdited.current = true; setName(event.target.value); }} placeholder="比如：雾港来信 · 第 12 集" />
        <div id="canvas-create-name-feedback" className="canvas-create-name-feedback" role={nameInvalid ? 'alert' : undefined} aria-live="polite" aria-atomic="true">
          {nameInvalid ? <><CircleAlert size={13} aria-hidden="true" /><span>请先填写画布名称</span></> : <span>起个名字，方便下次找到这块画布。</span>}
        </div>
      </div>
      <div className="canvas-create-choices" aria-label="选择画布起点">
        {CHOICES.map(([value, title, copy, Icon]) => <button type="button" aria-pressed={mode === value} disabled={busy || Boolean(created)} className={mode === value ? 'is-selected' : ''} key={value} onClick={() => { setMode(value); setError(''); setEpisodeError(''); }}><Icon size={18} /><strong>{title}</strong><small>{copy}</small><span className="canvas-create-choice-check" aria-hidden="true">{mode === value && <Check size={12} />}</span></button>)}
      </div>
      {mode === 'script' && <div className="canvas-script-source-switch" aria-label="剧本来源">
        {[['library', '我的剧本'], ['external', '外部剧本']].map(([value, label]) => <button type="button" key={value} aria-pressed={scriptSource === value}
          disabled={busy || Boolean(created)} className={scriptSource === value ? 'is-selected' : ''} onClick={() => { setScriptSource(value); setError(''); }}>{label}</button>)}
        <span>{externalScript ? '让已有的故事直接进入制作' : '从写作台继续你的故事'}</span>
      </div>}
      <div className={`canvas-create-source${libraryScript ? ' is-script' : ''}`}>
        {(libraryScript || mode === 'case') && <CanvasSourcePicker key={mode} kind={mode} items={mode === 'script' ? projects : cases}
          value={mode === 'script' ? projectId : caseId} disabled={busy || Boolean(created)} loading={loading} error={loadError}
          onRetry={() => setRetry(value => value + 1)} onChange={value => {
            if (mode === 'script') { if (value !== String(projectId)) { setProjectId(value); setEpisodeNumber(''); } }
            else setCaseId(value);
          }} />}
        {libraryScript && <div className="canvas-create-script-detail">
          {!projectId && <div className="canvas-create-script-hint"><Film size={20} aria-hidden="true" /><strong>选择剧本，继续制作</strong><p>找到你的故事，再选择要带入画布的已定稿集数。</p></div>}
          {episodeLoading && <p role="status"><Loader2 size={14} className="animate-spin" />正在加载可用集数…</p>}
          {episodeError && <p role="alert">{episodeError}<button type="button" onClick={() => setRetry(value => value + 1)}>重新加载</button></p>}
          {projectId && <label className="canvas-create-field"><span>制作哪一集</span><select aria-label="制作哪一集" value={episodeNumber} disabled={busy || episodeLoading || Boolean(created)} onChange={event => setEpisodeNumber(event.target.value)}><option value="">选择已定稿的集数</option>{availableEpisodes.map(item => <option key={item.episode_number} value={item.episode_number}>第 {item.episode_number} 集 · {item.title || '未命名'}</option>)}</select></label>}
          {projectId && !episodeLoading && !episodeError && !availableEpisodes.length && <p>这个项目还没有已定稿正文，请先在写作台采用一集初稿。</p>}
          {canProduceEpisode(episode) && <p className="canvas-create-summary">预检查通过后带入第 {episodeNumber} 集正文与人物资料。图片、视频由你确认后再生成。</p>}
        </div>}
        {externalScript && <ExternalScriptImport draft={externalDraft} selection={externalSelection} onChange={setExternalDraft} onReadingChange={setExternalReading}
          disabled={busy || Boolean(created)} preview={preview} />}
        {mode === 'case' && <>
          {selectedCase && <p className="canvas-create-summary">{selectedCase.sub_title || selectedCase.title} · 将创建案例素材与创作说明两个节点，进入后可继续改编。</p>}
        </>}
        {mode === 'import' && <>
          <label className="canvas-create-field"><span>选择媒体文件</span><input type="file" multiple accept={Object.values(UPLOAD_LIMITS).map(limit => limit.accept).join(',')} disabled={busy || Boolean(created)} onChange={chooseFiles} /></label>
          <p>最多 20 个；图片 30MB、视频 50MB、音频 10MB 以内。</p>
          <ul className="canvas-create-files">{files.map((item, index) => <li key={`${item.file.name}-${index}`}><span>{item.file.name}</span><small>{item.status === 'done' ? '已上传' : item.status === 'failed' ? item.error : '待上传'}</small></li>)}</ul>
        </>}
      </div>
      {mode === 'script' && <CanvasScriptPrecheck precheck={precheck} preview={preview} library={libraryScript} disabled={busy || Boolean(created)}
        onRetry={() => { setError(''); precheck.retry(); if (libraryScript) setRetry(value => value + 1); }} />}
      {error && <p className="canvas-create-error" role="alert">{error}</p>}
      </div>
      <footer className={mode === 'script' ? 'is-script' : undefined}><span role="status">{busy ? progress : preview ? '界面预览，登录后可创建' : mode === 'script' ? precheck.checking ? '正在预检查，请稍候' : precheck.passed ? '预检查通过，可开始制作' : '全部检查通过后可创建' : '创建后直接进入画布'}</span><div>
        {mode === 'script' && <button type="button" disabled={!ready} onClick={event => create(event, false)}>仅创建画布</button>}
        {created && !busy && <button type="button" onClick={() => onCreated(created)}>先进入画布</button>}
        <button className="is-primary" type="submit" disabled={!ready}>{busy ? <Loader2 size={14} className="animate-spin" /> : mode === 'script' ? <Film size={14} /> : <Plus size={14} />}{busy ? '正在处理…' : mode === 'script' ? '创建并拆镜' : created ? '重试失败文件' : '创建画布'}{!busy && <ArrowUpRight size={14} aria-hidden="true" />}</button>
      </div></footer>
    </form>
  </div>;
}
